import { useCallback, useEffect, useRef } from "react";
import { useToggle } from "react-use";

import { useSendFavourite } from "@/api/userRpc/favourites/hooks";
import { FavouritesQueryParams } from "@/api/userRpc/favourites/models";
import { useAuthContext } from "@/context/auth-renderer/Auth.context";
import { useOpenRegisterModal } from "@/hooks/useOpenRegisterModal";
import { useInvalidateLadyFavorite } from "@/utils/queries-invalidation";

type UseFavoritesProps = {
  objectId?: number;
  isFavorite?: boolean;
  favoriteType: "LADY" | "PHOTO" | "VIDEO";
  ignoreLocalState?: boolean;
};

type FavoriteActionType = FavouritesQueryParams["action"];

export const useFavorite = ({
  objectId,
  isFavorite,
  favoriteType,
  ignoreLocalState = false,
}: UseFavoritesProps) => {
  // FIXME: causes bugs. need to refactor and completely remove this state
  // need to rely ONLY on isFavorite from props
  const [isInFavorites, setIsInFavorites] = useToggle(isFavorite ?? false);
  useEffect(() => {
    if (isFirstRender.current) return;
    setIsInFavorites(isFavorite ?? false);
  }, [isFavorite, setIsInFavorites]);

  const { invalidate } = useInvalidateLadyFavorite();

  const onSuccess = useCallback(() => {
    setIsInFavorites();
    invalidate();
  }, [setIsInFavorites, invalidate]);

  const authContext = useAuthContext();
  const { openModalWithHeader } = useOpenRegisterModal();
  const isFirstRender = useRef(true);

  const { mutateAsync: sendFavourite, isLoading: isFavoriteLoading } =
    useSendFavourite({ onSuccess });

  const onFavouriteClick = useCallback(
    async (
      event: React.MouseEvent<HTMLButtonElement>,
    ): Promise<{ ok: boolean }> => {
      event.stopPropagation();
      if (!objectId) return { ok: false };

      const actionType = (
        ignoreLocalState
          ? isFavorite
            ? `${favoriteType}_REMOVE`
            : `${favoriteType}_ADD`
          : isInFavorites
          ? `${favoriteType}_REMOVE`
          : `${favoriteType}_ADD`
      ) satisfies FavoriteActionType;

      if (authContext.isAuthenticated) {
        try {
          await sendFavourite({
            action: actionType,
            userId: Number(authContext.userId),
            favoriteObjectId: objectId,
          });
          return { ok: true };
        } catch (e) {
          return { ok: false };
        }
      } else {
        openModalWithHeader("Add to Favorites")();
        return { ok: true };
      }
    },
    [
      objectId,
      ignoreLocalState,
      isFavorite,
      favoriteType,
      isInFavorites,
      authContext,
      sendFavourite,
      openModalWithHeader,
    ],
  );

  return {
    isInFavorites,
    isFavoriteLoading,
    onFavouriteClick,
  };
};
